<template>
    <FormSection :fullHeight="false" :applicationId="inquiry.application">
        <template v-slot:content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <span class="mt-1 big-text">
                    <b>Save Inquiry In Progress...</b>
                </span>
            </section>
            <section class="row p-2" v-if="!isLoading">
                <div class="col-12 offset-lg-1 col-lg-5 row pe-3">
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="contact-first-name"> Contact First Name </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="contact-first-name"
                            type="text"
                            placeholder="Enter Contact First Name"
                            v-model="inquiry.contactFirstName"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="contact-middle-initial" v-tooltip="{ title: 'Middle Initial' }"> M.I. </label>
                        <input
                            class="form-control"
                            :maxlength="1"
                            name="contact-middle-initial"
                            type="text"
                            placeholder="Enter M.I."
                            v-model="inquiry.contactMiddleInitial"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-4">
                        <label for="contact-last-name"> Contact Last Name </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="contact-last-name"
                            type="text"
                            placeholder="Enter Contact Last Name"
                            v-model="inquiry.contactLastName"
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="contact-phone-number"> Contact Phone Number</label>
                        <PhoneNumber
                            v-if="inquiry.contactPhoneNumbers?.length > 0"
                            :name="`contactPhoneNumbers`"
                            :enableCountry="true"
                            v-model:value="inquiry.contactPhoneNumbers[0].number"
                            v-model:country="inquiry.contactPhoneNumbers[0].country"
                            v-model:extension="inquiry.contactPhoneNumbers[0].extension"
                            v-model:type="inquiry.contactPhoneNumbers[0].type"
                            v-number-only
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="relationship-to-patient"> Relationship to Patient </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="contact-relationship-to-patient"
                            type="text"
                            placeholder="Enter Relationship"
                            v-model="inquiry.contactRelationshipToPatient"
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="agency-location-id">
                            Branch
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            class="form-select"
                            v-model="inquiry.locationId"
                            @change="selectedBranch(inquiry.locationId)"
                            @blur="v$.inquiry.locationId.$touch"
                            :class="`${!v$.inquiry.locationId.$error ? '' : 'field-invalid'}`"
                        >
                            <option :value="null">Select Branch</option>
                            <optgroup v-for="branch in branches" :label="branch.providerName" :key="branch.id">
                                <option :value="branch.id">{{ branch.name }}</option>
                            </optgroup>
                        </select>
                        <span v-if="v$.inquiry.locationId.$error" class="field-invalid">Branch is required.</span>
                    </div>
                    <div class="form-group col-12" v-if="inquiry.application === ApplicationEnum.AxxessHospiceFE">
                        <label for="agency-location-id" id="hospice-service-requested"> Services Requested </label>
                        <div class="flex-shrink-1 flex-grow-1">
                            <input
                                class="form-control"
                                name="service-requested"
                                type="text"
                                value="Hospice"
                                :disabled="true"
                            />
                        </div>
                    </div>
                    <div class="form-group col-12">
                        <label for="teamMember"> Assign Team Member to Follow Up </label>
                        <TypeaheadInput
                            :getData="getTeamList"
                            :value="teamMember"
                            v-on:valueChanged="changeTeamMember"
                            placeholder="Search Team Member"
                            :openOnFocus="openOnFocus"
                            :changeOpenOnFocus="changeOpenOnFocus"
                        />
                    </div>
                    <div class="form-group col-6">
                        <label for="date" class="d-inline-block"> Inquiry Date </label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    name="date"
                                    type="text"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    v-model="inquiry.date"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>
                    <div class="form-group col-6 ps-lg-2">
                        <label for="followUpDate" class="d-inline-block"> Follow-Up Date </label>
                        <DatePicker :getDate="changeDate">
                            <div class="input-group date datepickerInput">
                                <input
                                    name="followUpDate"
                                    type="text"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    v-model="inquiry.followUpDate"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>
                </div>
                <div class="col-12 col-lg-5 row ps-3">
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="patient-first-name">
                            Patient First Name
                            <span class="text-danger">*</span>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="patient-first-name"
                            type="text"
                            placeholder="Enter Patient First Name"
                            v-model="inquiry.firstName"
                            :class="`${!v$.inquiry.firstName.$error ? '' : 'field-invalid'}`"
                            @blur="v$.inquiry.firstName.$touch"
                        />
                        <span v-if="v$.inquiry.firstName.$error" class="field-invalid">First Name is required.</span>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label
                            for="patient-middle-initial"
                            v-tooltip="!isApplicationPalliative ? { title: 'Middle Initial' } : null"
                        >
                            {{ middleNameLabel }}
                        </label>
                        <input
                            class="form-control"
                            :maxlength="isApplicationPalliative ? 100 : 1"
                            name="patient-middle-initial"
                            type="text"
                            :placeholder="isApplicationPalliative ? 'Enter Middle Name' : 'Enter M.I.'"
                            v-model="inquiry.middleName"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="patient-last-name">
                            Patient Last Name
                            <span class="text-danger">*</span>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            name="patient-last-name"
                            type="text"
                            placeholder="Enter Patient Last Name"
                            v-model="inquiry.lastName"
                            :class="`${!v$.inquiry.lastName.$error ? '' : 'field-invalid'}`"
                            @blur="v$.inquiry.lastName.$touch"
                        />
                        <span v-if="v$.inquiry.lastName.$error" class="field-invalid">Last Name is required.</span>
                    </div>
                    <div class="form-group col-12">
                        <label for="phone-number">
                            Patient Phone Number
                            <span class="text-danger">*</span>
                        </label>
                        <PhoneNumber
                            :name="`phoneNumbers`"
                            :enableCountry="true"
                            v-model:value="inquiry.phoneNumbers[0].number"
                            v-model:country="inquiry.phoneNumbers[0].country"
                            v-model:extension="inquiry.phoneNumbers[0].extension"
                            v-model:type="inquiry.phoneNumbers[0].type"
                            v-number-only
                            v-on:blurredInput="v$.inquiry.phoneNumbers[0].number.$touch"
                            :class="`${!v$.inquiry.phoneNumbers[0].number.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.inquiry.phoneNumbers[0].number.$error" class="field-invalid"
                            ><span
                                v-if="v$.inquiry.phoneNumbers[0].number.$errors[0].$message == 'Value is required'"
                                class="field-invalid"
                                >Phone number is required.</span
                            >
                            <span v-else class="field-invalid">10 Digit phone is required.</span>
                        </span>
                    </div>
                    <div class="form-group col-12">
                        <label for="patient-current-location"> Patient Current Location </label>
                        <input
                            class="form-control"
                            maxlength="100"
                            name="patient-current-location"
                            type="text"
                            placeholder="Enter Patient Current Location"
                            v-model="inquiry.currentLocation"
                        />
                    </div>
                    <div class="form-group col-12">
                        <label for="inquiry-comments"> Comments </label>
                        <CommentInput
                            input-name="inquiry-comments"
                            placeholder="Enter Comments"
                            :result="inquiry.comment"
                            v-on:resultUpdatedAppended="resultUpdatedAppended"
                            v-on:resultUpdated="resultUpdated"
                            :application="inquiry.application"
                        />
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<style src="./InquiryForm.scss"></style>
<script lang="ts" src="./InquiryForm.ts"></script>
